import {
  MEDIA_TYPE,
  muteParticipant,
  pinParticipant,
  unpinParticipant,
  useAllParticipants,
  useIsParticipantPinned,
  useParticipantStore,
  useTrackMuted,
} from '@cometchat/calls-sdk-core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { IconName } from '../../icon/icon-paths-24x24';
import Icon from '../../icon/Icon.web';
import './MoreOptionButton.web.css';

interface MoreOptionButtonProps {
  ruid: string;
  closeMenu?: boolean;
  iconFill?: string;
}

interface MenuOption {
  label: string;
  onPress: () => void;
  iconName?: IconName;
  selected?: boolean;
}

const MoreOptionButton = ({
  ruid,
  closeMenu,
  iconFill,
}: MoreOptionButtonProps) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [menuPosition, setMenuPosition] = useState<'above' | 'below'>('below');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const participant = useAllParticipants().find(p => p.ruid === ruid);
  const localParticipant = useParticipantStore(state => state.localParticipant);
  const isPinnedParticipant = useIsParticipantPinned(
    participant?.pid ?? '',
    participant?.type ?? 'human',
  );
  const muted = useTrackMuted(MEDIA_TYPE.AUDIO, participant?.pid);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle force close from parent
  useEffect(() => {
    if (closeMenu && menuVisible) {
      setMenuVisible(false);
    }
  }, [closeMenu, menuVisible]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setMenuVisible(false);
      }
    };

    if (menuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuVisible]);

  const options: MenuOption[] = useMemo(() => {
    const opts: MenuOption[] = [
      {
        label: isPinnedParticipant ? 'Unpin' : 'Pin to screen',
        iconName: isPinnedParticipant ? 'unpin' : 'pin',
        onPress: () => {
          if (isPinnedParticipant) {
            unpinParticipant();
          } else if (participant) {
            pinParticipant(participant.pid, participant.type);
          }
          setMenuVisible(false);
        },
      },
    ];

    if (!participant) {
      return opts;
    }

    if (
      localParticipant.role === 'moderator' &&
      participant.pid !== localParticipant.pid &&
      participant?.type === 'human'
    ) {
      if (!muted) {
        opts.push({
          label: 'Mute Audio',
          iconName: 'mic-off',
          onPress: () => {
            muteParticipant(participant.pid);
            setMenuVisible(false);
          },
        });
      }
    }

    return opts;
  }, [
    isPinnedParticipant,
    participant,
    localParticipant.role,
    localParticipant.pid,
    muted,
  ]);

  if (!participant) {
    return null;
  }

  const handleButtonClick = () => {
    if (!menuVisible && buttonRef.current) {
      // Calculate button position relative to viewport
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const buttonCenterY = buttonRect.top + buttonRect.height / 2;
      const viewportMiddle = window.innerHeight / 2;

      // If button is above middle, show menu below; if below middle, show menu above
      const shouldShowAbove = buttonCenterY > viewportMiddle;
      setMenuPosition(shouldShowAbove ? 'above' : 'below');
    }
    setMenuVisible(!menuVisible);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setMenuVisible(false);
    }
  };

  return (
    <div className="cometchat-calls-more-option-container">
      <button
        ref={buttonRef}
        className={`cometchat-calls-more-option-button ${menuVisible ? 'cometchat-calls-more-option-button--active' : ''}`}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-label={`More options for ${participant.name}`}
        aria-expanded={menuVisible}
        aria-haspopup="menu"
        type="button">
        <Icon
          name="more"
          size={20}
          fill={
            iconFill || menuVisible
              ? 'var(--cometchat-calls-primary-button-icon)'
              : 'var(--cometchat-calls-icon-color-secondary)'
          }
        />
      </button>

      {menuVisible && (
        <>
          <div
            className="cometchat-calls-more-option-overlay"
            onClick={() => setMenuVisible(false)}
          />
          <div
            ref={menuRef}
            className={`cometchat-calls-more-option-menu ${isMobileView ? 'cometchat-calls-more-option-menu--mobile' : ''} ${
              menuPosition === 'above'
                ? 'cometchat-calls-more-option-menu--above'
                : ''
            }`}
            role="menu"
            aria-orientation="vertical">
            {options.map((option, index) => (
              <button
                key={index}
                className={`cometchat-calls-more-option-menu__item ${
                  option.selected
                    ? 'cometchat-calls-more-option-menu__item--selected'
                    : ''
                }`}
                onClick={option.onPress}
                role="menuitem"
                aria-label={option.label}
                type="button">
                {option.iconName && (
                  <Icon
                    name={option.iconName}
                    size={24}
                    fill="var(--cometchat-calls-icon-color-secondary)"
                  />
                )}
                <span className="cometchat-calls-more-option-menu__item-text">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MoreOptionButton;
